import { $createParagraphNode, $getEditor, $insertNodes, $isParagraphNode } from 'lexical';
import { FileText } from 'lucide-react';
import { SlashOption } from '../../../components/ui/slashMenuOptions';
import { $createPageLinkNode } from '../../../components/ui/lexicalNodes/PageLinkNode';
import { uid, createDocument, listTree, keyBetween, countOwnDocuments } from './documentsStore';
import { requireLocalCapacityFor } from '../../../plans/localGate';
import { isCapped } from '../../../plans/capRefusal';
import { bumpDocumentsTree } from './documentsUiStore';
import { openDocumentTab } from '../../../store/tabsStore';

/**
 * Persist the subpage record: last-of-siblings under the host document (the
 * sidebar's endKey idiom — keyBetween(lastSibling, null) — over a fresh
 * listTree read), with the PRE-GENERATED id the chip already carries.
 */
async function createSubpageRecord({ id, parentDocumentId, scope }) {
  // Plans v2: a subpage is a document. The slash option asks first on a
  // capped plan (no chip on a refusal); this is the backstop for any caller.
  if (scope?.type !== 'team' && !(await requireLocalCapacityFor('documents', countOwnDocuments))) {
    throw Object.assign(new Error('plan_limit'), { planLimit: true });
  }
  const nodes = await listTree(scope);
  const siblings = nodes
    .filter((n) => (n.parentId ?? null) === parentDocumentId)
    .sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
  const last = siblings[siblings.length - 1];
  await createDocument(
    { id, parentId: parentDocumentId, title: '', sortKey: keyBetween(last ? last.sortKey : null, null) },
    scope
  );
}

/**
 * The "Subpage" slash-menu extra for a document editor host: inserts a
 * PageLink chip pointing at a not-yet-created child document, then creates
 * that document (under `documentId`, in `scope`) and opens its tab.
 *
 * The returned SlashOption's onSelect runs INSIDE editor.update (see
 * SlashCommandPlugin.onSelectOption): the editor mutation — chip + trailing
 * paragraph — is synchronous; persistence is fire-and-forget. The id is
 * generated up front (documentsStore.createDocument honors caller ids) so the
 * chip is valid the moment it's inserted; if the create fails, the chip
 * naturally resolves as "Deleted page" and nothing navigates.
 *
 * Hosts MUST memoize the array they pass as `slashExtras` (see
 * LexicalRichEditor's prop note): DocumentEditorTab builds this in a useMemo
 * keyed on [documentId, scope].
 */
export function buildSubpageSlashExtra({ documentId, scope }) {
  const scopeType =
    scope?.type === 'team' && scope.teamId != null ? 'team' : scope?.type === 'device' ? 'device' : 'personal';
  const teamId = scopeType === 'team' ? scope.teamId : null;

  /** Insert the chip at the selection and create its document. Inside editor.update. */
  const insertAndCreate = () => {
    const newId = uid();
    const chip = $createPageLinkNode({ docId: newId, scopeType, teamId, title: 'Untitled' });
    $insertNodes([chip]);
    // Land the caret BELOW the chip, in a real paragraph, so typing
    // continues instead of the selection sitting on the decorator.
    const next = chip.getNextSibling();
    if (next && $isParagraphNode(next)) {
      next.selectStart();
    } else {
      const paragraph = $createParagraphNode();
      chip.insertAfter(paragraph);
      paragraph.select();
    }
    createSubpageRecord({ id: newId, parentDocumentId: documentId, scope })
      .then(() => {
        bumpDocumentsTree();
        openDocumentTab({ documentId: newId, label: 'Untitled', scope });
      })
      .catch((err) => {
        // The chip stays put and resolves as "Deleted page" — visible,
        // removable, honest about what didn't happen.
        console.warn('[documents] subpage create failed:', err);
      });
  };

  return new SlashOption('Subpage', {
    group: 'Document',
    description: 'Create a page inside this one',
    icon: FileText,
    keywords: ['subpage', 'page', 'child', 'nested'],
    onSelect: () => {
      // Plans v2: on a plan that caps documents, ask FIRST and insert the chip
      // only once the create is allowed, so a refused subpage leaves nothing
      // behind (it used to leave a dead "Deleted page" chip). onSelect runs
      // synchronously inside editor.update and the count is async, so the
      // capped path inserts in a later editor.update on the same editor, at
      // the selection the slash command left. Unlimited plans (and team
      // scopes) insert at once, as before.
      if (scope?.type !== 'team' && isCapped('documents')) {
        const editor = $getEditor();
        requireLocalCapacityFor('documents', countOwnDocuments)
          .then((ok) => {
            if (ok) editor.update(insertAndCreate);
          })
          .catch((err) => console.warn('[documents] subpage capacity check failed:', err));
        return;
      }
      insertAndCreate();
    },
  });
}

/** Tests only: the record write behind the Subpage slash option. */
export const createSubpageRecordForTests = createSubpageRecord;
